import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";

import { serverUrl } from "../../../../config";
import Pagination from "../../../../partials/pagination";
import NoData from "../../../../partials/noData";
import CompanyappLoader from "../../../../partials/companyappLoader";

import { Container, Row, Col } from "react-bootstrap";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";

// Two full rows of the 8-across grid below.
const PAGE_SIZE = 16;
const ANY = "";

// The card grid, shared by the results grid and the selection tray so both lay
// out identically. A CSS grid rather than Bootstrap columns because the target
// is 8 across and a 12-column grid has no eighth.
const CARD_GRID = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, 1fr)",
    sm: "repeat(3, 1fr)",
    md: "repeat(6, 1fr)",
    lg: "repeat(8, 1fr)",
  },
  gap: 2,
};

// Matches ../filters.js, so the controls here render identically to the ones
// on "Add slide from library".
const INPUT_FONT_SIZE = 14;
const INPUT_VARIANT = "outlined";

// How many hero documents to pull at once. Each is ~43KB of JSON parsed on the
// main thread purely to read one image URL, so firing a whole page of them at once
// stalls interaction for as long as they take to land. Six keeps the network
// busy while leaving the main thread responsive, and cards fill in as they
// arrive instead of all-or-nothing on the slowest response.
const THUMB_CONCURRENCY = 6;

// "Select All N results" walks the whole set a chunk at a time — the endpoint
// caps `limit` at 1000 — and gives up rather than sitting on a disabled button
// if the CMS never answers.
const SELECT_ALL_CHUNK = 200;
const SELECT_ALL_TIMEOUT = 30000;

// Hides both "Select All N results" buttons — the page one and the cross-page
// one. Hotels are picked a card at a time for now; the walk behind the buttons
// is left in place.
const SHOW_SELECT_ALL = false;

// The action buttons have to hold their label on a single line at every width
// rather than growing tall and narrow — Reset shares one grid slot with five
// dropdowns, and the selection pair sits in a row with the pagination.
// flex-basis lets them share a slot evenly; nowrap plus a content-derived
// min-width stops the label breaking instead of the button widening.
const ACTION_BTN_WRAP = { flex: "1 1 0", minWidth: "fit-content" };
const ACTION_BTN = { whiteSpace: "nowrap", px: 1.25, minWidth: 0 };

// The hotels endpoint has no image column — the hero lives in each hotel's
// stored document, which `detailUrl` serves as a static ~43KB file. Insert uses
// /hotel-detail instead, because only that carries the categorised `images` the
// block reads.
const heroFromStoredDoc = (doc) =>
  doc?.hotel?.content?.image?.binaryContent?.url ||
  doc?.content?.image?.binaryContent?.url ||
  "";

/**
 * One filter dropdown.
 *
 * Same shape as the Library/Language/Theme controls in ../filters.js: a
 * primary.light FormControl wrapping an outlined Select.
 *
 * Memoised, and fed a memoised `items` array plus a stable `onChange`, so the
 * parent's frequent re-renders — thumbnails streaming in, selections changing,
 * insert progress ticking — skip all five of these entirely instead of
 * re-rendering five MUI control shells each time.
 */
const FilterSelect = React.memo(function FilterSelect({
  id,
  label,
  value,
  onChange,
  items,
}) {
  return (
    <Col xs={12} sm={6} md={4} lg={2} className="filter-item mb-3">
      <FormControl
        fullWidth
        size="small"
        sx={{ backgroundColor: "primary.light" }}
      >
        <InputLabel
          sx={{ fontSize: INPUT_FONT_SIZE, color: "primary.main" }}
          size="small"
          id={`${id}-select-label`}
        >
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-select-label`}
          id={`${id}-select`}
          value={value}
          label={label}
          variant={INPUT_VARIANT}
          onChange={onChange}
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          input={
            INPUT_VARIANT === "outlined" ? (
              <OutlinedInput
                owner="admin"
                sub_variant="presentation-filter"
                sx={{ fontSize: INPUT_FONT_SIZE }}
                label={label}
              />
            ) : (
              <Input sx={{ fontSize: INPUT_FONT_SIZE }} label={label} />
            )
          }
        >
          <MenuItem value={ANY} sx={{ fontSize: INPUT_FONT_SIZE }}>
            Any
          </MenuItem>
          {items}
        </Select>
      </FormControl>
    </Col>
  );
});

/**
 * The free-text search box, deliberately a component of its own.
 *
 * It keeps the keystroke-by-keystroke value in its own state and only tells the
 * parent once typing settles. Holding that value on AddHotelSlide meant every
 * character re-rendered the entire modal — five MUI Selects, the button row,
 * the grid and the tray — and MUI recomputes emotion styles on each of those,
 * which is what made typing lag. Memoised so the parent's own re-renders
 * (thumbnails streaming in, selections changing) don't reach back into it.
 */
const HotelSearchField = React.memo(function HotelSearchField({ onSearch }) {
  const [value, setValue] = useState("");

  // Kept in a ref so changing the handler identity can't restart the timer
  // mid-type.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    const t = setTimeout(() => onSearchRef.current(value.trim()), 400);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <FormControl fullWidth size="small">
      <TextField
        id="hotel-title"
        label="Search Hotel Name"
        className="title-textfield"
        variant="standard"
        value={value}
        size="small"
        placeholder="Search hotel name"
        onChange={(event) => setValue(event.target.value)}
        InputProps={{
          style: { fontSize: INPUT_FONT_SIZE },
          endAdornment: (
            <InputAdornment position="end">
              {value.length > 0 ? (
                <IconButton
                  className="title-clear-btn"
                  onClick={() => setValue("")}
                >
                  <ClearIcon className="title-adornment-icon" />
                </IconButton>
              ) : (
                <SearchOutlined className="title-adornment-icon search" />
              )}
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ style: { fontSize: INPUT_FONT_SIZE } }}
      />
    </FormControl>
  );
});

/**
 * One hotel card.
 *
 * Renders as a bare grid item — the parent lays the cards out with CSS grid, so
 * the card carries no column sizing of its own.
 *
 * Memoised deliberately: a page holds 16 of these, and without it every
 * selection click re-rendered all 16 card subtrees. `onToggle` is passed the
 * hotel object so the parent can keep one stable callback rather than minting
 * 16 closures per render.
 */
const HotelCard = React.memo(function HotelCard({
  hotel,
  hero,
  active,
  onToggle,
}) {
  return (
    <Box
      className="hotel-slide-card"
      onClick={() => onToggle(hotel)}
      sx={{
        cursor: "pointer",
        border: active ? "3px solid" : "1px solid",
        borderColor: active ? "primary.main" : "#d8d8d8",
        borderRadius: 1,
        overflow: "hidden",
        height: "100%",
        transition: "border-color 0.15s ease-in-out",
      }}
    >
      {/* Always 16:9, whatever ratio the hero itself is: `cover` scales the
          image up until it fills the frame and the overflow is trimmed evenly
          off both edges, so the visible crop stays centred. */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          backgroundColor: "#eceff1",
          backgroundImage: hero ? `url("${hero}")` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box sx={{ padding: "8px 10px 12px" }}>
        {/* Not adminText.light — on the white card that renders almost
            invisible. The location line is a real label, not a hint. */}
        <Typography
          variant="caption"
          owner="admin"
          sx={{ display: "block", color: "#5f6b76" }}
        >
          {[hotel.city, hotel.country].filter(Boolean).join(", ")}
        </Typography>
        <Typography
          variant="body2"
          owner="admin"
          color="adminText.dark"
          // Clamped so a long hotel name can't stretch one card and knock the
          // whole row out of alignment.
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "3.75em",
          }}
        >
          {hotel.name}
        </Typography>
      </Box>
    </Box>
  );
});

export default function AddHotelSlide(props) {
  const [filterOptions, setFilterOptions] = useState(null);
  const [region, setRegion] = useState(ANY);
  const [country, setCountry] = useState(ANY);
  const [city, setCity] = useState(ANY);
  const [brand, setBrand] = useState(ANY);
  const [service, setService] = useState(ANY);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Bumped by Reset. The search box owns its in-progress value, so the only way
  // to clear it from out here is to give it a fresh mount.
  const [searchKey, setSearchKey] = useState(0);

  const [hotels, setHotels] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  // Hotels matching the current filters, across all pages. The endpoint counts
  // before it slices, so this is the real total rather than what came back.
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInserting, setIsInserting] = useState(false);
  // {done, total} while an insert runs. Each hotel is a /hotel-detail call plus
  // a create plus a patch, all serial, so a large selection takes a while and a
  // bare spinner reads as a hang.
  const [insertProgress, setInsertProgress] = useState({ done: 0, total: 0 });

  const [selected, setSelected] = useState([]);
  // Selecting the whole result set is a request of its own, and a slow one on a
  // wide filter.
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  // code -> hero image url. Kept across pages and filter changes so paging back
  // and forth doesn't refetch what we already have.
  const [thumbs, setThumbs] = useState({});
  const thumbsRef = useRef({});

  // O(1) membership for the card grid. `selected` stays an array because insert
  // order is the order slides get created in.
  const selectedCodes = useMemo(
    () => new Set(selected.map((h) => h.tms_hotel_code)),
    [selected],
  );

  // Stable across renders, so HotelCard's memoisation actually holds.
  const toggleHotel = useCallback((hotel) => {
    setSelected((prev) =>
      prev.some((h) => h.tms_hotel_code === hotel.tms_hotel_code)
        ? prev.filter((h) => h.tms_hotel_code !== hotel.tms_hotel_code)
        : [...prev, hotel],
    );
  }, []);

  const isMountedRef = useRef(true);
  useEffect(() => {
    // Set on the way in as well as cleared on the way out: a cleanup that only
    // ever flips it false leaves the ref stuck on a remount, and everything
    // guarded by it — insert progress included — then silently does nothing.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onSearch = useCallback((q) => {
    unstable_batchedUpdates(() => {
      setDebouncedSearch(q);
      setPage(1);
    });
  }, []);

  // ─── Filter options (one call, drives all five dropdowns) ────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(`${serverUrl}/api/v2/rhg/filter-options?_format=json`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setFilterOptions(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Hotels ──────────────────────────────────────────────────────────────
  // Shared by the grid and by "select all results", so the second one can ask
  // for exactly the set the first is paging through.
  const buildQuery = useCallback(
    (limit, pageNo) => {
      const params = new URLSearchParams({
        limit: String(limit),
        page: String(pageNo),
        _format: "json",
      });
      if (region) params.append("region", region);
      if (country) params.append("country", country);
      if (city) params.append("city", city);
      if (brand) params.append("brand", brand);
      if (service) params.append("services", service);
      if (debouncedSearch) params.append("q", debouncedSearch);
      return params.toString();
    },
    [region, country, city, brand, service, debouncedSearch],
  );

  const fetchHotels = useCallback(() => {
    setIsLoading(true);

    fetch(`${serverUrl}/api/v2/rhg/hotels?${buildQuery(PAGE_SIZE, page)}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        // React 17 doesn't batch state set from a promise callback, so without
        // this the three updates below are three separate render passes of the
        // whole grid.
        unstable_batchedUpdates(() => {
          setHotels(json?.collection || []);
          setPages(json?.pages || 1);
          setTotal(json?.total || 0);
          setIsLoading(false);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setHotels([]);
          setTotal(0);
          setIsLoading(false);
        });
      });
  }, [buildQuery, page]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  // Any filter change puts us back on page 1 — staying on page 9 of a result
  // set that now has two pages shows an empty grid.
  // One stable handler per filter, so FilterSelect's memoisation holds.
  //
  // Region and Country also clear what they narrow: leaving a Country selected
  // from a different Region filtered every hotel out, and left the Select
  // holding a value no longer present in its options.
  const onRegionChange = useCallback((event) => {
    setRegion(event.target.value);
    setCountry(ANY);
    setCity(ANY);
    setPage(1);
  }, []);

  const onCountryChange = useCallback((event) => {
    setCountry(event.target.value);
    setCity(ANY);
    setPage(1);
  }, []);

  const onCityChange = useCallback((event) => {
    setCity(event.target.value);
    setPage(1);
  }, []);

  const onBrandChange = useCallback((event) => {
    setBrand(event.target.value);
    setPage(1);
  }, []);

  const onServiceChange = useCallback((event) => {
    setService(event.target.value);
    setPage(1);
  }, []);

  // ─── Card thumbnails ─────────────────────────────────────────────────────
  // Pulled through a small worker pool and flushed on a timer: one render per
  // ~200ms of arrivals rather than one per hotel, which on React 17 (no
  // automatic batching in promises) would be one full grid render per hotel.
  useEffect(() => {
    let cancelled = false;
    // `.current` is only ever mutated, never reassigned, so holding the same
    // object for the life of the effect (cleanup included) is correct.
    const store = thumbsRef.current;
    const queue = hotels.filter(
      (h) => h.detailUrl && store[h.tms_hotel_code] === undefined,
    );
    if (!queue.length) return undefined;

    // Mark in-flight up front so a re-render mid-fetch doesn't queue the same
    // hotel twice. Anything still unresolved when this effect is torn down gets
    // un-marked below, otherwise changing filters mid-flight would leave those
    // hotels permanently image-less on the way back.
    queue.forEach((h) => {
      store[h.tms_hotel_code] = "";
    });
    const resolved = new Set();

    let flushTimer = null;
    const scheduleFlush = () => {
      if (flushTimer || cancelled) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        if (!cancelled) setThumbs({ ...store });
      }, 200);
    };

    let cursor = 0;
    const worker = () => {
      if (cancelled || cursor >= queue.length) return Promise.resolve();
      const h = queue[cursor++];
      return fetch(h.detailUrl)
        .then((r) => (r.ok ? r.json() : null))
        .then((doc) => {
          store[h.tms_hotel_code] = heroFromStoredDoc(doc);
          resolved.add(h.tms_hotel_code);
          scheduleFlush();
        })
        .catch(() => {})
        .then(worker);
    };

    Promise.all(
      Array.from({ length: Math.min(THUMB_CONCURRENCY, queue.length) }, worker),
    ).then(() => {
      if (!cancelled) setThumbs({ ...store });
    });

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
      queue.forEach((h) => {
        if (!resolved.has(h.tms_hotel_code)) {
          delete store[h.tms_hotel_code];
        }
      });
    };
  }, [hotels]);

  // ─── Cascading options ───────────────────────────────────────────────────
  // Memoised because these lists are large — 477 cities, 102 services, 93
  // countries — and rebuilding ~690 MenuItem elements on every keystroke was
  // the bulk of the typing lag.
  const regionItems = useMemo(
    () =>
      (filterOptions?.regions || []).map((r) => (
        <MenuItem key={r} value={r} sx={{ fontSize: INPUT_FONT_SIZE }}>
          {r}
        </MenuItem>
      )),
    [filterOptions],
  );

  const countryItems = useMemo(
    () =>
      (filterOptions?.countries || [])
        .filter((c) => !region || c.region === region)
        .map((c) => (
          <MenuItem key={c.name} value={c.name} sx={{ fontSize: INPUT_FONT_SIZE }}>
            {c.name}
          </MenuItem>
        )),
    [filterOptions, region],
  );

  const cityItems = useMemo(
    () =>
      (filterOptions?.cities || [])
        .filter(
          (c) =>
            (!country || c.country === country) &&
            (!region || c.region === region),
        )
        .map((c) => (
          <MenuItem
            key={`${c.name}-${c.country}`}
            value={c.name}
            sx={{ fontSize: INPUT_FONT_SIZE }}
          >
            {c.name}
          </MenuItem>
        )),
    [filterOptions, country, region],
  );

  const brandItems = useMemo(
    () =>
      (filterOptions?.brands || []).map((b) => (
        <MenuItem key={b.code} value={b.code} sx={{ fontSize: INPUT_FONT_SIZE }}>
          {b.name}
        </MenuItem>
      )),
    [filterOptions],
  );

  const serviceItems = useMemo(
    () =>
      (filterOptions?.services || []).map((s) => (
        <MenuItem key={s.key} value={s.key} sx={{ fontSize: INPUT_FONT_SIZE }}>
          {s.name}
        </MenuItem>
      )),
    [filterOptions],
  );

  const clearAll = () => setSelected([]);

  const filtersActive = Boolean(
    region || country || city || brand || service || debouncedSearch,
  );

  const resetFilters = useCallback(() => {
    unstable_batchedUpdates(() => {
      setRegion(ANY);
      setCountry(ANY);
      setCity(ANY);
      setBrand(ANY);
      setService(ANY);
      setDebouncedSearch("");
      setSearchKey((k) => k + 1);
      setPage(1);
    });
  }, []);

  // Appends rather than replaces, and skips what is already in — selection
  // survives paging, so the same hotel can easily come round twice.
  const addToSelection = useCallback((rows) => {
    setSelected((prev) => {
      const next = [...prev];
      const have = new Set(prev.map((h) => h.tms_hotel_code));
      rows.forEach((h) => {
        if (!have.has(h.tms_hotel_code)) next.push(h);
      });
      return next;
    });
  }, []);

  const selectAllOnPage = () => addToSelection(hotels);

  // Everything the current filters match, not just the page on screen.
  //
  // Walked in chunks rather than asked for in one go: the endpoint caps `limit`
  // at 1000, so a single limit=total request would silently hand back a short
  // list on any set bigger than that and the button would under-deliver on the
  // count in its own label.
  //
  // The button is disabled for the duration, so the one thing this must never
  // do is leave without clearing the flag — hence the abort timer and the
  // unconditional reset in the tail.
  const selectAllResults = useCallback(() => {
    if (!total || isSelectingAll) return;
    setIsSelectingAll(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SELECT_ALL_TIMEOUT);
    const rows = [];

    const fetchPage = (pageNo) =>
      fetch(
        `${serverUrl}/api/v2/rhg/hotels?${buildQuery(SELECT_ALL_CHUNK, pageNo)}`,
        { credentials: "include", signal: controller.signal },
      )
        .then((r) => {
          if (!r.ok) throw new Error(`hotels ${r.status}`);
          return r.json();
        })
        .then((json) => {
          const batch = json?.collection || [];
          rows.push(...batch);
          // Trust the response's own total over the one in state, which was
          // read on a different request.
          const expected = json?.total || 0;
          return batch.length === SELECT_ALL_CHUNK && rows.length < expected
            ? fetchPage(pageNo + 1)
            : rows;
        });

    fetchPage(1)
      .then((all) => {
        if (isMountedRef.current) addToSelection(all);
      })
      .catch((error) => {
        console.error("Select all hotels failed", error);
      })
      .then(() => {
        clearTimeout(timeout);
        if (isMountedRef.current) setIsSelectingAll(false);
      });
  }, [addToSelection, buildQuery, isSelectingAll, total]);

  // What the loader and the Insert button both say while a job runs. `done`
  // counts hotels finished, so the one being worked on is done + 1; once it
  // reaches the total the slides exist and what's left is the deck patch and
  // reload, which is its own few seconds.
  const insertLabel = useMemo(() => {
    const { done, total: count } = insertProgress;
    if (!count) return "Inserting...";
    if (done >= count) return "Finishing up...";
    if (count === 1) return "Inserting slide";
    return `Inserting ${done + 1} of ${count} slides`;
  }, [insertProgress]);

  const onInsert = () => {
    if (!selected.length || isInserting) return;

    const done = () => {
      // A successful insert reloads the deck, which unmounts this dialog before
      // the promise settles — so only touch state if we're still here.
      if (!isMountedRef.current) return;
      unstable_batchedUpdates(() => {
        setIsInserting(false);
        setInsertProgress({ done: 0, total: 0 });
      });
    };

    unstable_batchedUpdates(() => {
      setIsInserting(true);
      setInsertProgress({ done: 0, total: selected.length });
    });

    Promise.resolve(
      props.onAddHotelSlides(selected, (doneCount, total) => {
        if (isMountedRef.current) {
          setInsertProgress({ done: doneCount, total });
        }
      }),
    )
      .then(done)
      .catch(done);
  };


  return (
    <Dialog
      fullScreen={true}
      open={props.open}
      onClose={() => {
        if (!isInserting) props.onClose();
      }}
      className="presentation-library-modal hotel-slide-library-modal"
    >
      {/* The same loader the editor puts up while a library slide insert runs
          (editPresentation's `isLoading` overlay). That one is already running
          underneath — addHotelSlides sets loading on entry — but this dialog is
          above it, so it repeats it here. Fixed rather than absolute: it has to
          clear the AppBar's own stacking context. */}
      {isInserting && (
        <Box
          className="circular-container fs"
          sx={{
            position: "fixed",
            zIndex: (theme) => theme.zIndex.modal + 1,
            // Denser than the 75% white the class carries — over a
            // full-screen white dialog that veil barely registers as "busy,
            // nothing here is clickable" — while still showing what is behind
            // it. !important because that class wins on specificity.
            backgroundColor: "rgba(255, 255, 255, 0.9) !important",
            color: "#000",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
            px: 3,
          }}
        >
          <CircularProgress size={28} color="primary" />
          <Typography variant="h6" sx={{ color: "inherit" }}>
            {insertLabel}
          </Typography>
        </Box>
      )}

      <AppBar
        sx={{
          position: "relative",
          backgroundColor: "primary.light",
          color: "solid.dark",
        }}
      >
        <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Row className="w-100">
              <Col>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Dynamic Hotel Slide Library
                </Typography>
              </Col>
              <Col className="d-flex justify-content-end align-items-center">
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="cancel"
                        disabled={isInserting}
                        onClick={() => props.onClose()}
                        sx={{ marginRight: "10px !important" }}
                      >
                        <Typography variant="button" owner="admin" className="lh">
                          Cancel
                        </Typography>
                      </Button>
                      <Button
                        variant="contained"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="save"
                        className="lh"
                        disabled={!selected.length || isInserting}
                        startIcon={
                          isInserting ? (
                            <CircularProgress
                              size={16}
                              color="inherit"
                              // Indeterminate until the first hotel is
                              // reported, so the ring doesn't sit at 0%.
                              variant={
                                insertProgress.done
                                  ? "determinate"
                                  : "indeterminate"
                              }
                              value={
                                insertProgress.total
                                  ? (insertProgress.done /
                                      insertProgress.total) *
                                    100
                                  : 0
                              }
                            />
                          ) : null
                        }
                        onClick={onInsert}
                      >
                        <Typography variant="button" owner="admin" className="lh">
                          {isInserting
                            ? insertLabel
                            : `Insert${
                                selected.length ? ` (${selected.length})` : ""
                              }`}
                        </Typography>
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Toolbar>
        </DialogContent>
      </AppBar>

      <DialogContent
        sx={{ textAlign: isLoading && !hotels.length ? "center" : "" }}
      >
        <Container fluid className="add-library-container">
          <div className="ps-filters">
            <div className="page-header mb-1 mt-1">
              <Row>
                <Col
                  sm={12}
                  md={12}
                  lg={8}
                  className="title-container mb-1 mb-lg-0"
                >
                  <Row className="align-items-end">
                    <Col xs="auto">
                      <Typography
                        variant="h1"
                        owner="admin"
                        color="clientText.dark"
                        className="mb-0"
                      >
                        Choose hotel slide
                      </Typography>
                    </Col>
                  </Row>
                </Col>
                <Col sm={12} md={12} lg={4} className="general-filters">
                  <Stack
                    className="h-100"
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    <HotelSearchField key={searchKey} onSearch={onSearch} />
                  </Stack>
                </Col>
              </Row>
            </div>

            <Row className="justify-content-center">
              <Col xs={12}>
                <Row className="filter-container w-100 align-items-end justify-content-center">
                  <FilterSelect
                    id="region"
                    label="Region"
                    value={region}
                    onChange={onRegionChange}
                    items={regionItems}
                  />
                  <FilterSelect
                    id="country"
                    label="Country"
                    value={country}
                    onChange={onCountryChange}
                    items={countryItems}
                  />
                  <FilterSelect
                    id="city"
                    label="City"
                    value={city}
                    onChange={onCityChange}
                    items={cityItems}
                  />
                  <FilterSelect
                    id="brand"
                    label="Brand"
                    value={brand}
                    onChange={onBrandChange}
                    items={brandItems}
                  />
                  <FilterSelect
                    id="service"
                    label="Service"
                    value={service}
                    onChange={onServiceChange}
                    items={serviceItems}
                  />

                  {/* The row's sixth slot from xl up; a right-aligned row of
                      its own below that. The selection buttons live down by the
                      pagination, next to the results they act on. */}
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={2}
                    className="filter-item mb-3 align-self-end"
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                    >
                      <FormControl className="btn" sx={ACTION_BTN_WRAP}>
                        <Button
                          variant="outlined"
                          sx={ACTION_BTN}
                          disabled={!filtersActive}
                          onClick={resetFilters}
                        >
                          <Typography owner="admin" variant="button" className="lh">
                            Reset
                          </Typography>
                        </Button>
                      </FormControl>
                    </Stack>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Selecting the page sits with the filters because that is what
                changes what "the page" is. The count it refers to moved to the
                foot of the grid. Both buttons are off for now — see
                SHOW_SELECT_ALL. */}
            {SHOW_SELECT_ALL && total > 0 && (
              <Row className="mt-1">
                <Col>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      variant="outlined"
                      sx={{ ...ACTION_BTN, fontSize: INPUT_FONT_SIZE }}
                      disabled={!hotels.length}
                      onClick={selectAllOnPage}
                    >
                      <Typography owner="admin" variant="button" className="lh">
                        {`Select All ${hotels.length} results`}
                      </Typography>
                    </Button>

                    {/* Hidden for now. Takes every page behind the current
                        one, and was only offered once a selection was under
                        way and the results were cut down to a country. */}
                    {selected.length > 0 && country !== ANY && (
                      <Button
                        variant="outlined"
                        sx={{ ...ACTION_BTN, fontSize: INPUT_FONT_SIZE }}
                        disabled={isSelectingAll}
                        startIcon={
                          isSelectingAll ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : null
                        }
                        onClick={selectAllResults}
                      >
                        <Typography owner="admin" variant="button" className="lh">
                          {`Select All ${total} results`}
                        </Typography>
                      </Button>
                    )}
                  </Stack>
                </Col>
              </Row>
            )}
          </div>

          {isLoading && !hotels.length ? (
            <div className="circular-container fs t">
              <CompanyappLoader />
            </div>
          ) : (
            <>
              <Box className="mt-2" sx={{ position: "relative" }}>
                {hotels.length > 0 ? (
                  <>
                    {/* Paging/refiltering veils the current grid rather than
                        collapsing it to a spinner, so the scroll position
                        doesn't jump. Same as the library modal. */}
                    {isLoading && (
                      <Box
                        className="w-100 h-100"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          zIndex: 5,
                          background: "rgba(255, 255, 255, 1)",
                        }}
                      >
                        <CompanyappLoader />
                      </Box>
                    )}
                    <Box sx={CARD_GRID}>
                      {hotels.map((h) => (
                        <HotelCard
                          key={`grid-${h.tms_hotel_code}`}
                          hotel={h}
                          hero={thumbs[h.tms_hotel_code]}
                          active={selectedCodes.has(h.tms_hotel_code)}
                          onToggle={toggleHotel}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <NoData
                    activeIndex={0}
                    message1="There are no hotels matching those filters."
                  />
                )}
              </Box>

              <Row className="mt-1 mb-5">
                <Col
                  className="d-flex justify-content-center flex-wrap"
                  style={{ position: "relative" }}
                >
                  <Pagination
                    owner="admin"
                    containerClasses="creator-pagination d-flex justify-content-center align-items-center"
                    previousDisabled={page <= 1}
                    nextDisabled={page >= pages}
                    currentPage={page}
                    onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(pages, p + 1))}
                  />

                  {/* From md up it is taken out of the flow and pinned to the
                      right of the pagination row, so it reads as the foot of
                      the results without the numbers pushing the pager onto its
                      own line. `right` backs out the column's half-gutter — the
                      absolute containing block is the padding box, so right: 0
                      would hang the text a gutter past the edge of the cards.
                      pointer-events off so it can never eat a click meant for
                      Next.

                      Below md there isn't room for both across one line, so it
                      goes back into the flow at full width and wraps onto its
                      own row under the pager. */}
                  {total > 0 && (
                    <Typography
                      variant="body1"
                      owner="admin"
                      sx={{
                        position: { xs: "static", md: "absolute" },
                        width: { xs: "100%", md: "auto" },
                        mt: { xs: 1, md: 0 },
                        textAlign: "right",
                        right: {
                          md: "calc(var(--bs-gutter-x, 1.5rem) * 0.5)",
                        },
                        top: { md: "50%" },
                        transform: { xs: "none", md: "translateY(-50%)" },
                        color: "#5f6b76",
                        fontSize: 16,
                        fontWeight: 500,
                        pointerEvents: "none",
                      }}
                    >
                      {`Showing ${hotels.length} of ${total}`}
                    </Typography>
                  )}
                </Col>
              </Row>

              {selected.length > 0 && (
                <Box
                  sx={{
                    width: 1,
                    backgroundColor: "primary.light",
                    padding: "0 0.75rem",
                  }}
                >
                  <Row className="mt-2 pt-4 align-items-center">
                    <Col>
                      <h5 className="mb-0">
                        Selected hotel slides to insert:{" "}
                        <Typography
                          variant="span"
                          color="primary"
                          className="presentation-info"
                        >
                          {`${selected.length} selected`}
                        </Typography>
                      </h5>
                    </Col>
                    {/* Emptying the tray belongs with the tray, not with the
                        filter bar - up there it read as "clear the filters". */}
                    <Col xs="auto" className="d-flex justify-content-end">
                      <Button
                        variant="outlined"
                        owner="admin"
                        sx={ACTION_BTN}
                        disabled={isInserting}
                        onClick={clearAll}
                      >
                        <Typography owner="admin" variant="button" className="lh">
                          Clear All
                        </Typography>
                      </Button>
                    </Col>
                  </Row>
                  <Box sx={{ ...CARD_GRID, pt: 3, pb: 3 }}>
                    {selected.map((h) => (
                      <HotelCard
                        key={`selected-${h.tms_hotel_code}`}
                        hotel={h}
                        hero={thumbs[h.tms_hotel_code]}
                        active
                        onToggle={toggleHotel}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Container>
      </DialogContent>
    </Dialog>
  );
}
